import { useState, useMemo, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  flexRender,
  type ColumnDef,
  type SortingState,
  type ColumnFiltersState,
} from '@tanstack/react-table'
import type { Concert, QualityScore } from '@brent/shared/types'
import { useAuth } from '@/contexts/AuthContext'
import { useUpdateConcert } from '@/hooks/useConcerts'
import { useToast } from '@/hooks/use-toast'
import { QualityDropdown } from '@/components/shared/QualityDropdown'
import { Button } from '@brent/core-ui/ui'
import { Input } from '@brent/core-ui/ui'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@brent/core-ui/ui'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@brent/core-ui/ui'
import { ArrowUpDown, ChevronLeft, ChevronRight } from 'lucide-react'

export type ConcertFilter = 'all' | 'verified' | 'needs_review' | 'pending'

interface ConcertsTableProps {
  concerts: Concert[]
  filter: ConcertFilter
  onFilterChange: (filter: ConcertFilter) => void
}

export function ConcertsTable({ concerts, filter, onFilterChange }: ConcertsTableProps) {
  const navigate = useNavigate()
  const { canEdit } = useAuth()
  const updateConcert = useUpdateConcert()
  const { toast } = useToast()
  const [sorting, setSorting] = useState<SortingState>([{ id: 'year', desc: true }])
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([])
  const [decadeFilter, setDecadeFilter] = useState<string>('all')

  const decades = useMemo(() => {
    const decadeSet = new Set(concerts.map((c) => Math.floor(c.year / 10) * 10))
    return [...decadeSet].sort((a, b) => a - b)
  }, [concerts])

  const filteredConcerts = useMemo(
    () =>
      concerts.filter((c) => {
        // Quality filter
        if (filter !== 'all' && c.quality_score !== filter) return false
        // Decade filter
        if (decadeFilter !== 'all' && Math.floor(c.year / 10) * 10 !== Number(decadeFilter))
          return false
        return true
      }),
    [concerts, filter, decadeFilter],
  )

  const handleQualityChange = useCallback(
    async (concert: Concert, qualityScore: QualityScore) => {
      try {
        await updateConcert.mutateAsync({
          id: concert.id,
          input: { quality_score: qualityScore },
        })
        toast({ title: 'Updated', description: `Quality set to ${qualityScore}` })
      } catch (error) {
        toast({
          variant: 'destructive',
          title: 'Error',
          description: error instanceof Error ? error.message : 'Failed to update quality',
        })
      }
    },
    [updateConcert, toast],
  )

  const columns: ColumnDef<Concert>[] = useMemo(
    () => [
      {
        accessorKey: 'year',
        header: ({ column }) => (
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          >
            Date
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => {
          const concert = row.original
          return (
            <div>
              <span className="font-medium">{concert.date_text}</span>
              <span className="ml-2 text-xs text-muted-foreground">({concert.year})</span>
            </div>
          )
        },
        sortingFn: (rowA, rowB) => {
          const a = rowA.original
          const b = rowB.original
          if (a.year !== b.year) return a.year - b.year
          return (a.date_iso || '').localeCompare(b.date_iso || '')
        },
      },
      {
        accessorKey: 'venue_name',
        header: 'Venue',
        cell: ({ row }) => <span className="text-sm">{row.getValue('venue_name') || '-'}</span>,
      },
      {
        accessorKey: 'ensemble_name',
        header: 'Ensemble',
        cell: ({ row }) => (
          <span className="text-sm text-muted-foreground">
            {row.getValue('ensemble_name') || '-'}
          </span>
        ),
      },
      {
        accessorKey: 'programme_count',
        header: ({ column }) => (
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          >
            Items
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => (
          <span className="text-sm tabular-nums text-right block">
            {row.getValue('programme_count')}
          </span>
        ),
      },
      {
        accessorKey: 'source_sheet',
        header: 'Sheet',
        cell: ({ row }) => (
          <span className="text-sm text-muted-foreground">
            {row.getValue('source_sheet') || '-'}
          </span>
        ),
      },
      {
        accessorKey: 'quality_score',
        header: 'Status',
        cell: ({ row }) => {
          const concert = row.original
          if (canEdit) {
            return (
              <QualityDropdown
                value={concert.quality_score}
                onChange={(qs) => handleQualityChange(concert, qs)}
                disabled={updateConcert.isPending}
              />
            )
          }
          return (
            <span className="text-sm capitalize">{concert.quality_score.replace('_', ' ')}</span>
          )
        },
      },
    ],
    [canEdit, handleQualityChange, updateConcert.isPending],
  )

  const table = useReactTable({
    data: filteredConcerts,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    state: { sorting, columnFilters },
    initialState: {
      pagination: { pageSize: 25 },
    },
  })

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:flex-wrap">
        <Input
          placeholder="Search concerts..."
          value={(table.getColumn('venue_name')?.getFilterValue() as string) ?? ''}
          onChange={(e) => table.getColumn('venue_name')?.setFilterValue(e.target.value)}
          className="max-w-sm"
        />
        <Select value={decadeFilter} onValueChange={setDecadeFilter}>
          <SelectTrigger className="w-[140px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All decades</SelectItem>
            {decades.map((d) => (
              <SelectItem key={d} value={String(d)}>
                {d}s
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filter} onValueChange={(v) => onFilterChange(v as ConcertFilter)}>
          <SelectTrigger className="w-[160px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="verified">Verified</SelectItem>
            <SelectItem value="needs_review">Needs Review</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
          </SelectContent>
        </Select>
        <div className="sm:ml-auto text-sm text-muted-foreground">
          {table.getFilteredRowModel().rows.length} of {filteredConcerts.length} concerts
        </div>
      </div>

      <div className="overflow-x-auto rounded-md border">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead key={header.id}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(header.column.columnDef.header, header.getContext())}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  className="cursor-pointer"
                  onClick={() => navigate(`/concerts/${row.original.id}`)}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length} className="h-24 text-center">
                  No concerts found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <div className="flex items-center justify-between">
        <div className="text-sm text-muted-foreground">
          Page {table.getState().pagination.pageIndex + 1} of {table.getPageCount()}
        </div>
        <div className="flex items-center gap-2">
          <Select
            value={String(table.getState().pagination.pageSize)}
            onValueChange={(v) => table.setPageSize(Number(v))}
          >
            <SelectTrigger className="h-8 w-[80px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {[10, 25, 50, 100].map((size) => (
                <SelectItem key={size} value={String(size)}>
                  {size}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            size="sm"
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  )
}
